import { useState } from "react";
import { Download, Loader2, FileDown } from "lucide-react";
import { downloadRetainerComparisonPdf } from "@/lib/retainer-pdf";
import { RETAINER_PDF_VERSION } from "@/lib/retainer-pdf-version";
import { trackCTA } from "@/lib/tracking";

/**
 * One-click, client-side generated branded PDF comparing all FSAP retainer tiers.
 * No email gate — every visitor can take the comparison with them.
 */
export const RetainerPdfButton = ({ variant = "solid" }: { variant?: "solid" | "outline" }) => {
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    trackCTA("fsap_retainer_comparison_pdf", { source: "advocacy_pricing" });
    try {
      // Yield a frame so the spinner paints before the synchronous PDF build.
      await new Promise((r) => setTimeout(r, 16));
      downloadRetainerComparisonPdf();
    } finally {
      setBusy(false);
    }
  };

  const base =
    "inline-flex items-center gap-2 px-6 py-3 font-display uppercase tracking-[0.14em] text-xs transition-colors disabled:opacity-60";
  const styles =
    variant === "solid"
      ? "bg-gold text-bg-primary hover:bg-gold/90"
      : "border border-gold/50 text-gold hover:bg-gold/10";

  return (
    <div className="inline-flex flex-col items-start gap-1.5">
      <button type="button" onClick={handleClick} disabled={busy} className={`${base} ${styles}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
        {busy ? "Preparing PDF…" : "Download tier comparison (PDF)"}
        {!busy && <Download className="h-3.5 w-3.5 opacity-70" />}
      </button>
      <span className="text-[11px] tracking-wide text-muted-foreground">
        Current version v{RETAINER_PDF_VERSION} — always download fresh; older copies may be out of date.
      </span>
    </div>
  );
};
